import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { StandardService } from '@app/shared/services/standard.service';
import { augmentWithMilliTimestamp, MultiGpsMessage, RawGpsMessage, Truck } from '@cco/model';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { APP_CONFIG, AppConfig } from '@cco-apps/cco-frontend';

@Injectable()
export class TruckService extends StandardService<Truck, number> {
  constructor() {
    const config = inject<AppConfig>(APP_CONFIG);
    const http = inject(HttpClient);

    super(http, config.apiBaseUrl, `/trucks`);
  }

  /**
   * Latest GPS fix per asset. With `start`/`end` both supplied, returns each
   * asset's LAST fix within that window instead of its current-latest fix —
   * used to show historical mixer positions for a non-today selected day on
   * the Daily Dashboard. Assets with no fix in the window are simply absent.
   */
  getTrucksLatestGps$(start?: string, end?: string): Observable<MultiGpsMessage> {
    let params = new HttpParams();
    if (start != null && end != null) {
      params = params.set('start', start).set('end', end);
    }

    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http.get<MultiGpsMessage>(`${baseUrl + this.relativeUrl}/gps/latest`, { params }).pipe(
        map(response => this.mapMultiGpsMessage.bind(this)(response)),
      )),
    );
  }

  getTruckGps$(
    truckId: number,
    start: string,
    end: string,
    aggregation?: 'minute' | null,
  ): Observable<RawGpsMessage[]> {
    return this.tenantBaseUrl$.pipe(
      switchMap(baseUrl => this.http
      .get<RawGpsMessage[]>(`${baseUrl + this.relativeUrl}/${truckId}/gps`, {
        params: { start, end, aggregation },
      })
      .pipe(map(response => this.mapRawGpsMessages.bind(this)(response)))));
  }

  mapMultiGpsMessage(message: MultiGpsMessage): MultiGpsMessage {
    const augmented = {
      assets: message.assets,
    };
    for (const assetUuid in augmented.assets) {
      augmented.assets[assetUuid] = augmentWithMilliTimestamp(augmented.assets[assetUuid]);
    }

    return augmented;
  }

  mapRawGpsMessages(messages: RawGpsMessage[]): RawGpsMessage[] {
    return messages.map(m => augmentWithMilliTimestamp(m));
  }
}
